import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { withStyles } from '@mui/styles';
import { Button, Card, Divider, Grid, Typography } from '@mui/material';
import moment from 'moment';
import CircleIcon from '@mui/icons-material/Circle';

const AllocationsChart = ({ name, toggle, data, allocationPercentage }) => {
  const [years, setYears] = useState<any>([]);

  const CustomTableRow = withStyles({
    root: {
      backgroundColor: '#E6E9FF !important',
      height: '3rem',
      overflow: 'scrollable'
    }
  })(Card);

  const range = (min, max) =>
    [...Array(max - min + 1).keys()].map((i) => i + min);

  const calculateYears = useCallback(() => {
    const startYears = [
      ...new Set(
        data?.map((da) => {
          return Number(new Date(da?.startDate).getFullYear());
        })
      )
    ];

    const endYears = [
      ...new Set(
        data?.map((da) => {
          return Number(new Date(da?.endDate).getFullYear());
        })
      )
    ];

    const allYears: any = [...startYears, ...endYears];

    const yearRanges = range(Math.min(...allYears), Math.max(...allYears));

    setYears([...new Set([...yearRanges])].sort());
  }, [data]);

  useEffect(() => {
    if (data?.length > 0) {
      calculateYears();
    }
  }, [data, calculateYears]);

  const getProjectTimeline = (start, end, name, allocation, index) => {
    const startDate = moment(start);
    const endDate = moment(end);

    let dateDiff = endDate.diff(startDate, 'months');
    const startMonth = moment(start).format('M');
    let spacing = (Number(startMonth) - 1) * 4 + 8.5;
    const constantDateDiff = endDate.diff(startDate, 'months');

    if (Number(startMonth) + Number(dateDiff) > 12) {
      dateDiff = 12 - Number(startMonth);
    }

    const colors = [
      '#ebc44c',
      '#db4c64',
      '#a4d4cc',
      '#eeebe5',
      '#61dc87',
      '#250330',
      '#ed4ac9',
      '#bf754a',
      '#bf2128',
      '#fee1a4',
      '#0000ff',
      '#38761d'
    ];

    return (
      <Fragment>
        {spacing !== 0 && (
          <Typography
            style={{
              color: '#232C68',
              fontWeight: '700',
              fontSize: '11px',
              minWidth: `${spacing}rem`
            }}
          >
            &nbsp;
          </Typography>
        )}
        <Grid item>
          <Typography
            style={{
              color: '#232C68',
              fontWeight: '700',
              fontSize: '8px',
              textAlign: 'center'
            }}
          >
            {`${name} (${allocation}%)`}
          </Typography>
          <Grid item display={'flex'} alignItems={'center'}>
            <CircleIcon
              style={{ fontSize: '8px', color: `${colors[index]}` }}
            />
            <div
              style={{
                background: `${colors[index]}`,
                height: '3px',
                minWidth: `${dateDiff * 4 - 0.5}rem`
              }}
            />
            {Number(startMonth) + Number(constantDateDiff) < 13 && (
              <CircleIcon
                style={{ fontSize: '8px', color: `${colors[index]}` }}
              />
            )}
          </Grid>
        </Grid>
      </Fragment>
    );
  };

  const getProjectTimelineEnd = (end, name, allocation, index) => {
    const endingMonth = moment(end).format('M');
    let spacing = 8.5;

    const colors = [
      '#ebc44c',
      '#db4c64',
      '#a4d4cc',
      '#eeebe5',
      '#61dc87',
      '#250330',
      '#ed4ac9',
      '#bf754a',
      '#bf2128',
      '#fee1a4',
      '#0000ff',
      '#38761d'
    ];

    return (
      <Fragment>
        {spacing !== 0 && (
          <Typography
            style={{
              color: '#232C68',
              fontWeight: '700',
              fontSize: '11px',
              minWidth: `${spacing}rem`
            }}
          >
            &nbsp;
          </Typography>
        )}
        <Grid item>
          <Typography
            style={{
              color: '#232C68',
              fontWeight: '700',
              fontSize: '8px',
              textAlign: 'center'
            }}
          >
            {`${name} (${allocation}%)`}
          </Typography>
          <Grid item display={'flex'} alignItems={'center'}>
            <div
              style={{
                background: `${colors[index]}`,
                height: '3px',
                minWidth: `${(Number(endingMonth) - 1) * 4}rem`
              }}
            />
            <CircleIcon
              style={{ fontSize: '8px', color: `${colors[index]}` }}
            />
          </Grid>
        </Grid>
      </Fragment>
    );
  };

  const getProjectTimelineMiddle = (name, allocation) => {
    const endingMonth = 12;
    let spacing = 8.5;

    let bgColor = '#7393B3';
    if (name === 'Obexmetrix') {
      bgColor = '	#50C878	';
    } else if (name === 'EMS') {
      bgColor = '	#FFBF00';
    }

    return (
      <Fragment>
        {spacing !== 0 && (
          <Typography
            style={{
              color: '#232C68',
              fontWeight: '700',
              fontSize: '11px',
              minWidth: `${spacing}rem`
            }}
          >
            &nbsp;
          </Typography>
        )}
        <Grid item>
          <Typography
            style={{
              color: '#232C68',
              fontWeight: '700',
              fontSize: '8px',
              textAlign: 'center'
            }}
          >
            {`${name} (${allocation}%)`}
          </Typography>
          <Grid item display={'flex'} alignItems={'center'}>
            <div
              style={{
                background: `${bgColor}`,
                height: '3px',
                minWidth: `${(Number(endingMonth) - 1) * 4}rem`
              }}
            />
          </Grid>
        </Grid>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Grid sx={{ p: 2 }}>
        <Typography
          style={{
            color: '#232C68',
            fontWeight: '700',
            fontSize: '14px',
            marginBottom: '1rem'
          }}
        >{allocationPercentage === 0 ? `${name} is not assign to any project.` : `${name}` }
        </Typography>
        {years.map((year) => (
          <Fragment>
            <CustomTableRow
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '1rem'
              }}
            >
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '8rem'
                }}
              >
                {year}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Jan'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Feb'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Mar'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Apr'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'May'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Jun'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Jul'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Aug'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Sep'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Oct'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Nov'}
              </Typography>
              <Typography
                style={{
                  color: '#232C68',
                  fontWeight: '700',
                  fontSize: '11px',
                  minWidth: '4rem'
                }}
              >
                {'Dec'}
              </Typography>
            </CustomTableRow>
            {data?.length > 0 &&
              data.map(
                (ele, index) =>
                  new Date(ele?.startDate).getFullYear() === Number(year) && (
                    <Grid
                      item
                      display="flex"
                      justifyContent="start"
                      sx={{ p: 2 }}
                      key={ele.id}
                    >
                      {getProjectTimeline(
                        ele.startDate,
                        ele.endDate,
                        ele.projectName,
                        ele.allocation,
                        index
                      )}
                    </Grid>
                  )
              )}
            {data?.length > 0 &&
              data.map(
                (ele, index) =>
                  new Date(ele?.startDate).getFullYear() !==
                    new Date(ele?.endDate).getFullYear() &&
                  new Date(ele?.endDate).getFullYear() === Number(year) && (
                    <Grid
                      item
                      display="flex"
                      justifyContent="start"
                      sx={{ p: 2 }}
                      key={ele.id}
                    >
                      {getProjectTimelineEnd(
                        ele.endDate,
                        ele.projectName,
                        ele.allocation,
                        index
                      )}
                    </Grid>
                  )
              )}
            {data?.length > 0 &&
              data.map(
                (ele) =>
                  Number(new Date(ele?.startDate).getFullYear()) <
                    Number(year) &&
                  Number(new Date(ele?.endDate).getFullYear()) >
                    Number(year) && (
                    <Grid
                      item
                      display="flex"
                      justifyContent="start"
                      sx={{ p: 2 }}
                      key={ele.id}
                    >
                      {getProjectTimelineMiddle(
                        ele.projectName,
                        ele.allocation
                      )}
                    </Grid>
                  )
              )}
          </Fragment>
        ))}
        <Divider />
        <Grid
          item
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
          xs={12}
        >
          <Button
            sx={{
              width: '10%',
              fontWeight: 'normal',
              background: '#F5F7FF',
              fontSize: '13px'
            }}
            onClick={toggle}
          >
            Close
          </Button>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default AllocationsChart;
